/* eslint-disable react-hooks/exhaustive-deps */
import { Currencies, WalletTypes } from '@constants/enums';
import authSvc from '@efex-services/auth/authSvc';
import clientSvc from '@efex-services/client/clientSvc';
import {
  IPaymentsBalance,
  IPaymentsBalanceResponse,
} from '@efex-services/client/types';
import mixpanelSvc from '@efex-services/mixpanel/mixpanelSvc';
import { useRole } from '@efexteam/permission-manager';
import { useApiCall } from '@efexteam/use-api-call';
import useBoolean from '@main-hooks/useBoolean';
import { fetchUserProfile } from '@pages/profile/store';
import { selectProfileData } from '@pages/profile/store/selectors';
import { camelCaseMapData } from '@utils/formater';
import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Smartlook from 'smartlook-client';
import { useAppDispatch } from '@/store';
import { fetchWallets } from '@/modules/wallets/store';
import { fetchCombinedBalance } from '@/modules/combined_balance/store';

const useDashboard = ({
  setOpenPaymentModal,
}: {
  setOpenPaymentModal: (open: boolean) => void;
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userProfile = useSelector(selectProfileData);
  const { isAwActive } = userProfile || {};
  const { isReadOnly } = useRole();

  const addFundsCurrency = useRef<Currencies | undefined>();
  const addFundsWalletType = useRef<WalletTypes>(WalletTypes.GLOBAL_ACCOUNT);

  const {
    data: chartsData,
    loading: chartsLoading,
    invoke: getPaymentsBalance,
  } = useApiCall((enterpriseId: string | undefined) => {
    if (!enterpriseId) {
      throw new Error('Enterprise ID is required');
    }
    return clientSvc
      .getPaymentsBalance(enterpriseId)
      .then((x) =>
        camelCaseMapData<IPaymentsBalanceResponse, IPaymentsBalance>(x)
      );
  });

  const {
    data: paymentLinkData,
    loading: paymentLinkLoading,
    error: paymentLinkError,
    invoke: enablePaymentLink,
  } = useApiCall((enterpriseId: string | undefined) => {
    if (!enterpriseId) {
      throw new Error('Enterprise ID is required');
    }
    return authSvc.enablePaymentLink(enterpriseId);
  });

  const { enterpriseId } = useMemo(() => {
    const company = userProfile?.company;

    return {
      enterpriseId: company?.enterpriseId || null,
      country: company?.country || null,
    };
  }, [userProfile]);

  const [
    isAddFundsModalOpen,
    { setTrue: showAddFundsModal, setFalse: hideAddFundsModal },
  ] = useBoolean(false);

  const [
    paymentOutModal,
    { setFalse: hidePaymentOutModal, setTrue: showPaymentOutModal },
  ] = useBoolean(false);

  const openPopupDocumentKYB = () => {
    navigate('/onboarding');
  };

  const openPopupMakeNew = () => {
    if (isReadOnly) {
      return;
    }
    if (!userProfile?.isFullRegister) {
      openPopupDocumentKYB();
      return;
    }
    showPaymentOutModal();
  };

  const openPopupAddFunds = () => {
    if (!userProfile?.isFullRegister) {
      openPopupDocumentKYB();
      return;
    }
    addFundsCurrency.current = undefined;
    addFundsWalletType.current = WalletTypes.GLOBAL_ACCOUNT;
    showAddFundsModal();
  };

  const handleClosePaymentModal = () => {
    hidePaymentOutModal();
    setOpenPaymentModal(false);
    if (enterpriseId) {
      dispatch(fetchWallets(enterpriseId));
      dispatch(fetchCombinedBalance(enterpriseId));
      getPaymentsBalance(enterpriseId);
    }
  };

  const handleAddFunds = (currency: Currencies, walletType?: WalletTypes) => {
    addFundsCurrency.current = currency;
    addFundsWalletType.current = walletType ?? WalletTypes.GLOBAL_ACCOUNT;
    showAddFundsModal();
  };

  useEffect(() => {
    if (enterpriseId) {
      dispatch(fetchCombinedBalance(enterpriseId));
      getPaymentsBalance(enterpriseId);
    }
  }, []);

  useEffect(() => {
    const showValidationModal = sessionStorage.getItem('filesUploaded');
    if (showValidationModal) {
      openPopupDocumentKYB();
      sessionStorage.removeItem('filesUploaded');
    }
  }, []);

  useEffect(() => {
    if (userProfile?.legalRepresentative) {
      const { legalRepresentative, company, isTestUser } = userProfile;
      const { email } = legalRepresentative;

      if (!isTestUser && email) {
        // Mixpanel setup
        mixpanelSvc.identify(email);
        mixpanelSvc.people.set(legalRepresentative);

        // Smartlook setup
        if (Smartlook.initialized()) {
          Smartlook.navigation(pathname);
          Smartlook.identify(email, {
            ...legalRepresentative,
            enterpriseId: company?.enterpriseId ?? '',
          });
        }
      }
    }
  }, [userProfile]);

  useEffect(() => {
    if (
      userProfile &&
      !userProfile.company.isActivePaymentLink &&
      enterpriseId
    ) {
      enablePaymentLink(enterpriseId);
    }
  }, [userProfile, enterpriseId]);

  useEffect(() => {
    if (paymentLinkData && !paymentLinkLoading && !paymentLinkError) {
      dispatch(fetchUserProfile());
    }
  }, [paymentLinkData, paymentLinkLoading, paymentLinkError]);

  return {
    addFundsCurrency,
    addFundsWalletType,
    chartsData,
    chartsLoading,
    isAddFundsModalOpen,
    isAwActive,
    paymentOutModal,
    userProfile,
    handleAddFunds,
    handleClosePaymentModal,
    hideAddFundsModal,
    openPopupAddFunds,
    openPopupMakeNew,
  };
};

export default useDashboard;
